import { useAudioQueueStore } from '@/lib/audio-queue-store';
import { useBookmarkStore } from '@/lib/bookmark-store';
import {
  completeOwnerAdoptionCleanupRow,
  getOwnerAdoptionCleanupRow,
} from '@/lib/db';
import { useHighlightStore } from '@/lib/highlight-store';
import { clearOwnerAccountCheck } from '@/lib/owner-account-check';
import { reloadOwnerScopedSettingsMemory } from '@/lib/settings-store';
import { useAdiaStore } from '@/lib/store';
import { useSyncStore } from '@/lib/sync-store';

const OWNER_SCOPED_LOCAL_STORAGE_KEYS = [
  'adia-prayer-draft',
  'adia-prayer-draft-audio-ids',
  'adia-interrupted-interim',
  'adia-who-merge',
  'adia-who-dismissed-pairs',
  'adia-leaf-glow-seen',
  'adia-last-discovery',
  'adia-last-sync',
] as const;

function clearOwnerScopedLocalStorage(): void {
  for (const key of OWNER_SCOPED_LOCAL_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }
  localStorage.setItem('adia-cloud-account', '1');
  if (OWNER_SCOPED_LOCAL_STORAGE_KEYS.some((key) => localStorage.getItem(key) !== null)) {
    throw new Error('owner-browser-state-cleanup-failed');
  }
}

function clearOwnerScopedMemory(): void {
  useAdiaStore.setState({
    prayers: [],
    isLoading: false,
    hydration: { state: 'not-started', generation: 0 },
  });
  useBookmarkStore.setState({ bookmarks: [], isLoading: false });
  useHighlightStore.setState({ highlights: [], isLoading: false });
  useAudioQueueStore.setState({
    items: [],
    pendingCount: 0,
    totalBytes: 0,
    hydrated: false,
  });
  useSyncStore.setState({ isInitialSyncing: false });
  reloadOwnerScopedSettingsMemory();
}

/**
 * Finish the durable cleanup handshake before owner-bearing routes mount.
 * Language, text size, privacy default, Bible preferences, and public Bible
 * caches are deliberately preserved.
 */
export async function finishOwnerAdoptionCleanup(ownerId: string): Promise<boolean> {
  const pendingOwner = await getOwnerAdoptionCleanupRow();
  if (pendingOwner === null) {
    // The durable marker is browser-global, but each tab owns a separate
    // memory graph. Every document invalidates that graph before `ok`, even
    // when another tab already completed the one-time storage cleanup.
    clearOwnerScopedMemory();
    return false;
  }
  if (pendingOwner !== ownerId) throw new Error('owner-browser-state-owner-mismatch');
  clearOwnerScopedLocalStorage();
  // The tab-scoped keepsake locator is intentionally not owner data. It is
  // an opaque, non-capability job id whose server lookup is owner-gated; keep
  // it through fresh-profile adoption so an emailed link works on a new
  // browser after sign-in.
  clearOwnerAccountCheck();
  clearOwnerScopedMemory();
  await completeOwnerAdoptionCleanupRow(ownerId);
  return true;
}

export { OWNER_SCOPED_LOCAL_STORAGE_KEYS };
